import { getVehicleTemplateFieldValue } from "./vehicleTemplateValues";
import type { TemplateDictionaryOptionsMap } from "./templateDictionaryOptions";
import { CreatableSelect } from "@/components/features/matter/file-repo/GenerateDocument/CreatableSelect";
import {
  getBaseKeyForVariable,
  useAutoSelectSingleOption,
  type InstanceGroup,
  type VehicleVariablePrefix,
} from "@/components/features/matter/file-repo/GenerateDocument/GenerateDocumentSlideover";
import type { MatterVehicle } from "@/lib/types/Matter";
import { formatVehicleTitle } from "@/lib/utils/format";
import {
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction,
} from "react";

interface ApplyVehicleDropdownProps {
  templateDictionaryOptions: TemplateDictionaryOptionsMap;
  sectionKey: VehicleVariablePrefix;
  applySuffix: string;
  selectionKey: string;
  selectionValue: string;
  defaultItemId?: string;
  autoSelectSingleOption?: boolean;
  group: InstanceGroup;
  setSectionContactSelections: Dispatch<SetStateAction<Record<string, string>>>;
  setSelectedTemplateDictionaryOptions: Dispatch<
    SetStateAction<Record<string, string>>
  >;
  clientVehicles: MatterVehicle[];
  defendantVehicles: MatterVehicle[];
}

export const ApplyVehicleDropdown = ({
  sectionKey,
  templateDictionaryOptions,
  applySuffix,
  selectionKey,
  selectionValue,
  defaultItemId,
  autoSelectSingleOption = true,
  group,
  setSectionContactSelections,
  setSelectedTemplateDictionaryOptions,
  clientVehicles,
  defendantVehicles,
}: ApplyVehicleDropdownProps) => {
  const placeholder =
    sectionKey === "clientVehicle"
      ? `Apply Client Vehicle${applySuffix}`
      : `Apply Defendant Vehicle${applySuffix}`;

  // Build options for the vehicle section quick selector
  const clientVehicleSectionOptions = useMemo(
    () =>
      clientVehicles.map((vehicle) => ({
        value: vehicle.id,
        label: formatVehicleTitle(vehicle),
        context: vehicle.vin ? `VIN: ${vehicle.vin}` : "",
      })),
    [clientVehicles],
  );

  const defendantVehicleSectionOptions = useMemo(
    () =>
      defendantVehicles.map((vehicle) => ({
        value: vehicle.id,
        label: formatVehicleTitle(vehicle),
        context: vehicle.vin ? `VIN: ${vehicle.vin}` : "",
      })),
    [defendantVehicles],
  );

  const options =
    sectionKey === "clientVehicle"
      ? clientVehicleSectionOptions
      : defendantVehicleSectionOptions;

  const applySectionVehicleInstance = useCallback(
    (vehicleId: string) => {
      const vehicles =
        sectionKey === "clientVehicle" ? clientVehicles : defendantVehicles;
      const vehicle = vehicles.find((item) => item.id === vehicleId);
      if (!vehicle) return;

      const updates: Record<string, string> = {};
      group.items.forEach((item) => {
        const base = item.baseVariable || item.variable;
        if (!base.startsWith(sectionKey)) return;
        const targetKey = getBaseKeyForVariable(item.variable);
        const value = getVehicleTemplateFieldValue(
          vehicle,
          base,
          sectionKey,
          templateDictionaryOptions,
        );
        updates[targetKey] = value;
      });

      if (Object.keys(updates).length) {
        setSelectedTemplateDictionaryOptions((prev) => ({
          ...prev,
          ...updates,
        }));
      }
    },
    [
      clientVehicles,
      defendantVehicles,
      group,
      templateDictionaryOptions,
      sectionKey,
      setSelectedTemplateDictionaryOptions,
    ],
  );

  const handleChange = useCallback(
    (val: string) => {
      setSectionContactSelections((prev) => ({
        ...prev,
        [selectionKey]: val,
      }));
      applySectionVehicleInstance(val);
    },
    [applySectionVehicleInstance, selectionKey, setSectionContactSelections],
  );

  useAutoSelectSingleOption(
    defaultItemId
      ? options.filter((option) => option.value === defaultItemId)
      : options,
    selectionValue,
    handleChange,
    autoSelectSingleOption,
  );

  return (
    <div className="min-w-[260px]">
      <CreatableSelect
        className="!space-y-0"
        placeholder={placeholder}
        options={options}
        value={selectionValue}
        onChange={handleChange}
        createMessage="Create vehicle"
      />
    </div>
  );
};
